import * as pdfjsLib from "pdfjs-dist"
import { useCallback, useEffect, useRef } from "react"
import { GlobalWorkerOptions } from "pdfjs-dist/build/pdf"
import workerSrc from "pdfjs-dist/build/pdf.worker.min.js?url"

GlobalWorkerOptions.workerSrc = workerSrc

const PDFPreview = ({ file, embed = false, isMobile = false }) => {
  const pagesRef = useRef(null)
  const roRef = useRef(null)

  const getAvailWidth = () => {
    const el = pagesRef.current
    if (!el) return 900
    const host = el.parentElement || el
    const rect = host.getBoundingClientRect()
    const style = getComputedStyle(host)
    const padX = parseFloat(style.paddingLeft || "0") + parseFloat(style.paddingRight || "0")
    const w = Math.max(0, rect.width - padX)
    return w || 900
  }

  const clearPages = () => {
    if (pagesRef.current) pagesRef.current.innerHTML = ""
  }

  const renderPdf = useCallback(async (fileToRender) => {
    const target = pagesRef.current
    if (!fileToRender || !target) return

    await new Promise((r) => requestAnimationFrame(r))
    await new Promise((r) => requestAnimationFrame(r))

    const avail = getAvailWidth()

    const reader = new FileReader()
    reader.onload = async () => {
      const pdfData = new Uint8Array(reader.result)
      const pdf = await pdfjsLib.getDocument(pdfData).promise

      const pages = pagesRef.current
      if (!pages) return
      pages.innerHTML = ""
      pages.style.paddingTop = "4px"
      pages.style.paddingBottom = "16px"
      pages.style.width = "100%"

      for (let n = 1; n <= pdf.numPages; n++) {
        const page = await pdf.getPage(n)
        const base = page.getViewport({ scale: 1 })

        const scale = Math.max(avail / base.width, 0.1)
        const vp = page.getViewport({ scale })

        const dpr = Math.max(1, window.devicePixelRatio || 1)
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")
        canvas.width = Math.round(vp.width * dpr)
        canvas.height = Math.round(vp.height * dpr)
        canvas.style.width = `${vp.width}px`
        canvas.style.height = `${vp.height}px`

        canvas.style.display = "block"
        canvas.style.borderRadius = "12px"
        canvas.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"
        canvas.style.margin = "0 0 16px 0"
        canvas.style.background = "#fff"

        await page.render({
          canvasContext: ctx,
          viewport: vp,
          transform: [dpr, 0, 0, dpr, 0, 0],
        }).promise

        pages.appendChild(canvas)
      }
    }
    reader.readAsArrayBuffer(fileToRender)
  }, [])

  useEffect(() => {
    if (file) renderPdf(file)
    return () => clearPages()
  }, [file, renderPdf])

  useEffect(() => {
    const pages = pagesRef.current
    if (!pages) return
    const host = pages.parentElement || pages
    roRef.current = new ResizeObserver(() => {
      if (file) renderPdf(file)
    })
    roRef.current.observe(host)
    return () => {
      roRef.current?.disconnect()
      roRef.current = null
    }
  }, [file, renderPdf])

  if (!file) return null

  if (embed) {
    return <div ref={pagesRef} className="w-full mx-auto h-[68vh]" />
  }

  return (
    <div
      className="w-full rounded-3xl overflow-hidden flex flex-col"
      style={{ height: isMobile ? "auto" : "74vh", maxHeight: isMobile ? "auto" : "680px" }}
    >
      <div className="flex-1 overflow-auto" style={{ overscrollBehavior: "contain" }}>
        <div ref={pagesRef} className="w-full mx-auto px-4" />
      </div>
    </div>
  )
}

export default PDFPreview
